import { useSearchParams } from 'react-router';
import { RiSegmentEvents } from '../constants';
import { resetPageSearchParam } from '@/utils/purge-search-params.util';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';

/**
 * @typedef {Object} Option
 * @property {string} id - The unique identifier for the option.
 * @property {string} marketingName - The marketing name of the option.
 * @property {string} name - The property name displayed in the property filter as a fallback when the marketing name is unavailable.
 */
type Option = { id: string; marketingName: string; name: string };

type OptionWithSelected = Omit<Option, 'name'> & {
  selected: boolean;
};

export function useDynamicFilter(filterKey: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { track } = useAnalyticsContext();
  function getNumberOfSelectedFilterItems() {
    const filterStr = searchParams.get(filterKey);

    if (!filterStr) {
      return 0;
    }

    return filterStr.split(',').length;
  }

  function getFilterOptions(options: Option[]) {
    const filterStr = searchParams.get(filterKey);

    if (!filterStr) {
      const result = options.map((option) => ({
        ...option,
        marketingName: option.marketingName || option.name,
        selected: false,
      }));
      return result;
    }

    const filters = filterStr.split(',');

    const result = options.map((option) => ({
      ...option,
      marketingName: option.marketingName || option.name,
      selected: filters.includes(option.id),
    }));
    return result;
  }

  function handleFilterChange(options: OptionWithSelected[]) {
    const selectedOptionIds = options
      .filter((option) => option.selected)
      .map((option) => option.id);

    track(RiSegmentEvents.RiApplicationTableFilterClick, {
      filterKey,
      selectedOptions: selectedOptionIds,
    });

    setSearchParams((prevSearchParams) => {
      const selectedOptionIds = options
        .filter(({ selected }) => Boolean(selected))
        .map((option) => option.id);

      resetPageSearchParam(prevSearchParams);

      if (!selectedOptionIds.length) {
        prevSearchParams.delete(filterKey);

        return prevSearchParams;
      }

      prevSearchParams.set(filterKey, selectedOptionIds.join(','));

      return prevSearchParams;
    });
  }

  return {
    getFilterOptions,
    handleFilterChange,
    getNumberOfSelectedFilterItems,
  };
}
